import { Link } from "react-router-dom";
import { MicrophoneStage, WaveSine, ChartLineUp, Flame } from "@phosphor-icons/react";
import FounderBadge from "@/components/FounderBadge";

export default function Landing() {
  return (
    <div className="min-h-screen bg-bone-100 text-ink-900" data-testid="landing-page">
      {/* Header */}
      <header
        className="sticky top-0 z-20 bg-bone-100/70 backdrop-blur-xl border-b border-bone-300"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 font-serif text-xl min-w-0">
            <span className="w-2 h-2 rounded-full bg-brand inline-block shrink-0"></span>
            <span>Hush</span>
            <span className="hidden sm:inline text-ink-500 text-sm font-sans">— voice journal</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <Link
              to="/auth"
              className="btn-ghost text-sm whitespace-nowrap px-3 sm:px-4"
              data-testid="header-signin-link"
            >
              Sign in
            </Link>
            <Link
              to="/auth?mode=signup"
              className="btn-primary text-sm whitespace-nowrap px-3 sm:px-4"
              data-testid="header-getstarted-link"
            >
              <span className="hidden sm:inline">Start journaling</span>
              <span className="sm:hidden">Start</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-50 pointer-events-none"
          aria-hidden="true"
          style={{
            background:
              "radial-gradient(ellipse at 70% 30%, rgba(199,169,139,0.45) 0%, rgba(242,235,227,0) 60%), radial-gradient(ellipse at 20% 80%, rgba(183,93,70,0.18) 0%, rgba(242,235,227,0) 55%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 pt-20 pb-24 grid md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-7 animate-fade-up">
            <p className="eyebrow mb-6">A daily voice ritual</p>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl font-light leading-[1.05] tracking-tight">
              Speak it. <br />
              <span className="italic text-brand whitespace-nowrap">Understand it.</span>
            </h1>
            <p className="mt-8 max-w-xl text-lg text-ink-500 leading-relaxed">
              Hush turns your spoken thoughts into a quiet, intelligent journal — transcribed, tagged
              with mood, and gently reflected back to you each week.
            </p>
            <div className="mt-8">
              <FounderBadge />
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/auth?mode=signup" className="btn-primary" data-testid="hero-start-btn">
                Begin a 30‑second entry
              </Link>
              <Link to="/auth" className="btn-ghost" data-testid="hero-signin-btn">
                I already have an account
              </Link>
            </div>
            {/* Store badges — App Store + Google Play. Both link to the
                live listings. App Store: Apple App ID 6769325168. Play Store:
                package app.hush.journal. We use SVG-based badges per each
                store's marketing guidelines (single-color "Download" style,
                dark fill on the bone background). */}
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <p className="text-xs uppercase tracking-widest text-ink-500 w-full">Now on iPhone, iPad & Android</p>
              <a
                href="https://apps.apple.com/app/id6769325168"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download Hush on the App Store"
                className="inline-flex items-center gap-2 bg-ink-900 text-bone-100 hover:bg-ink-700 transition-all rounded-xl px-4 py-2.5"
                data-testid="appstore-badge"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M17.05 12.04c-.03-2.93 2.39-4.34 2.5-4.41-1.36-1.99-3.48-2.26-4.24-2.29-1.81-.18-3.53 1.06-4.45 1.06-.92 0-2.34-1.04-3.85-1.01-1.98.03-3.81 1.15-4.83 2.92-2.06 3.57-.53 8.85 1.48 11.75.99 1.42 2.16 3.02 3.7 2.96 1.49-.06 2.05-.96 3.85-.96 1.8 0 2.31.96 3.88.93 1.6-.03 2.61-1.45 3.59-2.88 1.13-1.65 1.6-3.26 1.62-3.34-.04-.02-3.11-1.19-3.14-4.73zM14.4 4.34c.82-.99 1.37-2.37 1.22-3.74-1.18.05-2.6.78-3.45 1.77-.76.87-1.43 2.27-1.25 3.62 1.31.1 2.66-.66 3.48-1.65z"/>
                </svg>
                <div className="flex flex-col leading-none text-left">
                  <span className="text-[10px] opacity-80">Download on the</span>
                  <span className="text-base font-medium tracking-tight">App Store</span>
                </div>
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=app.hush.journal"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get Hush on Google Play"
                className="inline-flex items-center gap-2 bg-ink-900 text-bone-100 hover:bg-ink-700 transition-all rounded-xl px-4 py-2.5"
                data-testid="playstore-badge"
              >
                {/* Google Play triangle — official 4-color in Google's
                    guidelines, but to keep our dark-on-bone aesthetic
                    consistent with the App Store badge we render it as
                    monochrome with subtle inner gradient via SVG defs. */}
                <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
                  <defs>
                    <linearGradient id="gp-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="currentColor" stopOpacity="0.95" />
                      <stop offset="100%" stopColor="currentColor" stopOpacity="0.7" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M3.6 2.4c-.4.4-.6 1-.6 1.7v15.8c0 .7.2 1.3.6 1.7l9.3-9.3-9.3-9.9zm10.3 10.4l2.9 2.9-7.3 4.2c-.5.3-1.1.3-1.5 0l5.9-7.1zm0-1.6L8.5 4l7.3 4.2-2.9 3zm6.6 4.2l-3-1.7 3.4-3.4c.5.4.8 1 .8 1.7s-.3 1.3-.8 1.7l-.4-.3z"
                    fill="url(#gp-grad)"
                  />
                </svg>
                <div className="flex flex-col leading-none text-left">
                  <span className="text-[10px] opacity-80">Get it on</span>
                  <span className="text-base font-medium tracking-tight">Google Play</span>
                </div>
              </a>
            </div>
          </div>

          <div className="md:col-span-5 animate-fade-up">
            <div className="card p-8 grain">
              <div className="flex items-center justify-between mb-6">
                <span className="eyebrow">Today · Tuesday</span>
                <span className="text-xs text-ink-500">0:42</span>
              </div>
              <div className="flex items-end justify-center gap-1 h-20 mb-6">
                {[0.4, 0.7, 0.5, 0.9, 0.6, 1, 0.7, 0.4, 0.8, 0.5, 0.9, 0.6, 0.3].map((h, i) => (
                  <div
                    key={`wf-${i}-${h}`}
                    className="wf-bar"
                    style={{ height: `${h * 80}px` }}
                  />
                ))}
              </div>
              <p className="font-serif italic text-ink-700 leading-relaxed">
                "Felt clearer after walking this morning. Still thinking about the conversation with
                Sam — there was something I didn't say."
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                <span className="px-3 py-1 rounded-full text-xs bg-mood-reflective/15 text-ink-900">reflective</span>
                <span className="px-3 py-1 rounded-full text-xs bg-mood-calm/15 text-ink-900">calm</span>
                <span className="px-3 py-1 rounded-full text-xs bg-mood-anxious/15 text-ink-900">restless</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-bone-300 bg-bone-200/40">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20">
          <p className="eyebrow mb-3">What lives inside</p>
          <h2 className="font-serif text-3xl sm:text-4xl max-w-2xl">
            A small daily practice with a slow, thoughtful payoff.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            <Feature icon={<MicrophoneStage size={28} weight="light" />} title="One‑tap recording" desc="Tap, talk, done. Up to ten minutes per entry." />
            <Feature icon={<WaveSine size={28} weight="light" />} title="Auto transcription" desc="Whisper turns your voice into searchable text." />
            <Feature icon={<ChartLineUp size={28} weight="light" />} title="Mood patterns" desc="Each entry is tagged. See how your week truly felt." />
            <Feature icon={<Flame size={28} weight="light" />} title="Gentle streaks" desc="A daily nudge — never a guilt trip." />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-24 text-center">
        <p className="eyebrow mb-4">Three minutes a day</p>
        <h3 className="font-serif text-4xl sm:text-5xl font-light max-w-3xl mx-auto leading-tight">
          The cheapest therapy is honest <span className="italic text-brand">noticing</span>.
        </h3>
        <Link to="/auth?mode=signup" className="btn-primary mt-10 inline-block" data-testid="cta-start-btn">
          Create your journal
        </Link>
      </section>

      <footer className="border-t border-bone-300 py-8 text-center text-sm text-ink-500">
        <div className="flex items-center justify-center gap-5 flex-wrap">
          <span>Built quietly · Hush</span>
          <Link to="/privacy" className="hover:text-ink-900" data-testid="footer-privacy-link">Privacy</Link>
          <Link to="/terms" className="hover:text-ink-900" data-testid="footer-terms-link">Terms</Link>
        </div>
      </footer>
    </div>
  );
}

function Feature({ icon, title, desc }) {
  return (
    <div className="card card-hover p-6">
      <div className="text-brand mb-4">{icon}</div>
      <h4 className="font-serif text-lg mb-2">{title}</h4>
      <p className="text-sm text-ink-500 leading-relaxed">{desc}</p>
    </div>
  );
}
